import { useEffect, useState } from "react";
import { Box, Button, Typography, Checkbox, FormControlLabel, Grid } from "mui";
import { CheckboxProps } from "@mui/material/Checkbox";
import { useNavigate, useParams } from "react-router-dom";
import { Field } from "formik";
import { Formik, Form } from "formik";

import clsx from "clsx";
import AppCard from "components/AppCard/AppCard";
import styles from "modules/Home/views/ProviderFiltersView/ProviderFiltersView.module.scss";
import {
  fetchAllProviders,
  fetchAllProvidersCards,
  fetchAllProvidersCollection,
} from "store/providers/thunks";
import { useDispatch } from "react-redux";
import { useAppSelector } from "utils/hooks/storeHooks";
import {
  selectProviders,
  selectProvidersCards,
} from "store/providers/selectors";
import { cardType } from "types/Provider";
import { selectSessionCredentials } from "store/session/selectors";
import { setEmbedToken } from "store/session";
import { getFilterRolePermission } from "store/profile/thunks";
import { setTaxonomyOptionsByRoleRequestStatus } from "store/profile";
import { selectProfileFilterRole } from "store/profile/selectors";
import { Permission } from "types/FilterRole";
import { TemplateFormValues } from "types/Template";
import { Autocomplete, TextField } from "@mui/material";
import {
  selectCities,
  selectCounties,
  selectStates,
} from "store/state/selectors";
import {
  getCitiesAsFilterOptions,
  getCountiesAsFilterOptions,
  getStatesAsFilterOptions,
} from "store/state/thunks";
import { physician, common_skills, organization_types } from "constants/CodeMaps";
import { useDebounce } from "utils/hooks/useDebounce";
import { getGeolocation } from "store/state/thunks";
import { selectGeolocation } from "store/state/selectors";

const ProviderFiltersView = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const locationProviderId = 3;
  const isLocationProvider =
    id !== undefined && Number(id) === locationProviderId;
  const providerTypeOfCards = useAppSelector(selectProvidersCards);
  const providers = useAppSelector(selectProviders);
  const { permission } = useAppSelector(selectProfileFilterRole);
  const [cardIdSelected, setCardIdSelected] = useState<number>();
  const { profile } = useAppSelector(selectSessionCredentials);
  const states = useAppSelector(selectStates);
  const counties = useAppSelector(selectCounties);
  const cities = useAppSelector(selectCities);
  const statesOptions = states.map((state) => ({
    label: state.name,
    value: state.code,
    id: state.id,
  }));
  const countiesOptions = counties.map((county) => ({
    label: county.name,
    value: county.name,
    id: county.id,
  }));
  const citiesOptions = cities.map((city) => ({
    label: city.name,
    value: city.name,
  }));

  const geolocation = useAppSelector(selectGeolocation);

  const [searchedState, setSearchedState] = useState<string>("");
  const [searchedCounty, setSearchedCounty] = useState<string>("");
  const [searchedCity, setSearchedCity] = useState<string>("");
  const [debouncedState] = useDebounce(searchedState, 200);
  const [debouncedCounty] = useDebounce(searchedCounty, 200);
  const [debouncedCity] = useDebounce(searchedCity, 200);

  const [stateId, setStateId] = useState(0);
  const [countyId, setCountyId] = useState(0);
  const cardSelectedInfo = providerTypeOfCards.find(
    (card: cardType) => card.id === cardIdSelected
  );
  let defaultTaxonomyFilters: string[] = [];
  const parsedFilterRoles = permission?.map((item) => ({
    ...item,
    providerId: Number(item.providerId),
  }));
  const filterRolePermissions: Permission[] | undefined =
    parsedFilterRoles?.filter(
      ({ providerId }: any) => String(providerId) === id
    );
  let collections: string[] = findCollectionByCardId(
    cardIdSelected,
    filterRolePermissions
  );
  const handleChangeStateSearchBar = (county: string) =>
    setSearchedState(county);
  const handleChangeCountySearchBar = (county: string) =>
    setSearchedCounty(county);
  const handleChangeCitySearchBar = (city: string) => setSearchedCity(city);
  function findCollectionByCardId(
    cardIdSelected?: number,
    filterRolePermissions?: Permission[]
  ): string[] {
    let collections: string[] = [];

    if (filterRolePermissions) {
      filterRolePermissions.forEach((permission) => {
        permission.filters.forEach((filter) => {
          if (filter.cardId === cardIdSelected || filter.cardId === null) {
            collections.push(...filter.collection);
          }
        });
      });
    }

    return collections;
  }

  const cardsToShow = providerTypeOfCards.filter((object: any) => {
    const filter = filterRolePermissions?.find((filtro: any) => {
      return filtro.filters.some((filter: any) => filter.cardId === object.id);
    });

    return filter !== undefined;
  });
  const providerSelected = providers.find(
    (provider: cardType) => String(provider?.id) === id
  );

  if (profile.taxonomy !== null && profile.taxonomy.length > 0) {
    defaultTaxonomyFilters = profile.taxonomy;
  }

  const initialValues: TemplateFormValues = {
    providerId: Number(id),
    cardId: cardIdSelected,
    cardValue: "",
    checkboxes: providerSelected?.id === 4 ? defaultTaxonomyFilters : [],
    reportId: providerSelected?.reportId,
    states: profile.state || { label: "California", value: "CA", id: 5 },
    counties: profile.county || null,
    cities: profile.city || null,
  };

  const mapCheckboxValuesToCodes = (values: TemplateFormValues) => {
    const taxonomyCodeMap: { [key: string]: string[] } = common_skills;
    const physicianCodeMap: { [key: string]: string[] } = physician;
    const organizationMap : { [key: string] : string[]} = organization_types;

    let codes: string[] = [];
    // console.log(values.checkboxes);
    if (values.checkboxes) {
      values.checkboxes.forEach((checkbox) => {
        if (values.providerId === 4 && taxonomyCodeMap[checkbox]) {
          codes = [...codes, ...taxonomyCodeMap[checkbox]];
        } else if (values.providerId === 1 && values.cardId === 1 && physicianCodeMap[checkbox]) {
          codes = [...codes, ...physicianCodeMap[checkbox]];
        } else if (values.providerId === 1 && values.cardId === 2) {
            if (organizationMap[checkbox]){
              codes = [...codes, ...organizationMap[checkbox]];
            } else{
              codes.push(checkbox);
            }
        }
      });
    }

    values.checkboxes = codes;
    return values;
  };

  const handleOnClickCard = (
    cardId: number,
    setFieldValue: (field: string, value: number) => void,
    values: TemplateFormValues
  ) => {
    setCardIdSelected(cardId);
    setFieldValue("cardId", cardId);
    /*     if (isLocationProvider) {
      handleSubmit(values);
    } */
  };

  const handleGoNext = (values: TemplateFormValues) => {
    let mapped_values = mapCheckboxValuesToCodes(values);
    // console.log("Mapped values ", mapped_values);
    navigate("/Graphs", { state: mapped_values });
  };

  const handleGoBack = () => {
    navigate("/Providers");
  };

  const handleSubmit = (values: TemplateFormValues) => {
    handleGoNext({
      ...values,
      cardValue:
        cardSelectedInfo?.enumerationType || (isLocationProvider && "NPI-2"),
      states:
        typeof values?.states === "string"
          ? values.states
          : values?.states?.value,
      counties:
        typeof values?.counties === "string"
          ? values.counties
          : values?.counties?.value,
      cities:
        typeof values?.cities === "string"
          ? values.cities
          : values?.cities?.value,
      geolocation,
    });
  };
  useEffect(() => {
    dispatch(getStatesAsFilterOptions(debouncedState));
  }, [dispatch, debouncedState]);
  useEffect(() => {
    dispatch(getCountiesAsFilterOptions(stateId, debouncedCounty));
  }, [dispatch, debouncedCounty, stateId]);
  useEffect(() => {
    dispatch(getCitiesAsFilterOptions(countyId, debouncedCity));
  }, [dispatch, debouncedCity, countyId]);

  useEffect(() => {
    if (id) {
      dispatch(fetchAllProvidersCards(id));
      dispatch(fetchAllProvidersCollection(id));
      dispatch(fetchAllProviders());
      dispatch(setEmbedToken(""));
      dispatch(getStatesAsFilterOptions());
      dispatch(getCountiesAsFilterOptions());
      dispatch(getCitiesAsFilterOptions());
      dispatch(getGeolocation(profile.address));
    }
  }, [dispatch, id, cardIdSelected]);

  useEffect(() => {
    if (id) {
      dispatch(
        getFilterRolePermission(
          profile.filterRoleId,
          id,
          setTaxonomyOptionsByRoleRequestStatus
        )
      );
    }
  }, [dispatch, id, profile.filterRoleId]);

  return (
    <Box className={styles.background}>
      <Box className={styles.pageContainer}>
        <Box className={styles.pageInfo}>
          <Typography className={styles.pageDescription} variant="h2">
            {`What type of ${
              providerSelected?.title?.split(" ")[1]
            } would you like to look for?`}
          </Typography>
        </Box>
        <Formik initialValues={initialValues} onSubmit={handleSubmit}>
          {({ setFieldValue, values }) => {
            if (
              values.states &&
              typeof values.states !== "string" &&
              values.states.id
            ) {
              setStateId(values.states.id);
            }
            if (
              values.counties &&
              typeof values.counties !== "string" &&
              values.counties.id
            ) {
              setCountyId(values.counties.id);
            }
            if (values?.states === null || values?.states === "") {
              if (values) {
                values.counties = null;
                values.cities = null;
              }
            }
            if (values?.counties === null || values?.counties === "") {
              if (values) {
                values.cities = null;
              }
            }
            return (
              <Form>
                <Box
                  className={clsx(
                    styles.pageContent,
                    cardsToShow.length % 2 === 0 ? "" : styles.pageContentOdd
                  )}
                >
                  {cardsToShow && (
                    <Grid container spacing={3}>
                      {cardsToShow.map(
                        (
                          { id, title, description, iconName }: cardType,
                          index: number
                        ) => (
                          <Grid key={index} item>
                            <AppCard
                              description={description}
                              iconName={iconName}
                              id={id}
                              isSelected={cardIdSelected === id}
                              title={title}
                              onClick={(id) =>
                                handleOnClickCard(id, setFieldValue, values)
                              }
                            />
                          </Grid>
                        )
                      )}
                    </Grid>
                  )}
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Field name="states">
                        {({ field }: any) => (
                          <Autocomplete
                            {...field}
                            className={styles.statesFilter}
                            options={statesOptions}
                            getOptionLabel={(option: any) => option.label}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="State"
                                variant="outlined"
                                onChange={(e) =>
                                  handleChangeStateSearchBar(e.target.value)
                                }
                              />
                            )}
                            onChange={(e, value) =>
                              setFieldValue("states", value)
                            }
                          />
                        )}
                      </Field>
                    </Grid>
                    <Grid item xs={4}>
                      <Field name="counties">
                        {({ field }: any) => (
                          <Autocomplete
                            {...field}
                            className={styles.statesFilter}
                            disabled={
                              values.states === "" || values.states === null
                            }
                            options={countiesOptions}
                            getOptionLabel={(option: any) => option.label}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="County"
                                variant="outlined"
                                onChange={(e) =>
                                  handleChangeCountySearchBar(e.target.value)
                                }
                              />
                            )}
                            onChange={(e, value) => {
                              setFieldValue("counties", value);
                              if (
                                values?.states === null ||
                                values?.states === ""
                              ) {
                                setFieldValue("counties", null);
                                setFieldValue("cities", null);
                              }
                            }}
                          />
                        )}
                      </Field>
                    </Grid>
                    <Grid item xs={4}>
                      <Field name="cities">
                        {({ field }: any) => (
                          <Autocomplete
                            {...field}
                            className={styles.statesFilter}
                            disabled={
                              values.states === "" ||
                              values.states === null ||
                              values.counties === "" ||
                              values.counties === null
                            }
                            options={citiesOptions}
                            getOptionLabel={(option: any) => option.label}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="City"
                                variant="outlined"
                                onChange={(e) =>
                                  handleChangeCitySearchBar(e.target.value)
                                }
                              />
                            )}
                            onChange={(e, value) => {
                              setFieldValue("cities", value);
                              if (
                                values?.counties === null ||
                                values?.counties === ""
                              ) {
                                setFieldValue("cities", null);
                              }
                            }}
                          />
                        )}
                      </Field>
                    </Grid>
                  </Grid>
                  {collections && (
                    <Grid
                      className={styles.checkboxContainer}
                      container
                      spacing={2}
                    >
                      {collections.length !== 0 && (
                        <Grid item xs={12}>
                          <Typography variant="h3">
                            {`Select the ${providerSelected?.title
                              ?.split(" ")[1]
                              .toLowerCase()} you want to view`}
                          </Typography>
                        </Grid>
                      )}
                      <Grid item xs={6} className={styles.checkboxGroupLeft}>
                        {collections
                          ?.slice(0, Math.ceil(collections.length / 2))
                          ?.map((title: string) => (
                            <div key={title}>
                              <Field
                                name="checkboxes"
                                type="checkbox"
                                value={title}
                              >
                                {({
                                  field: { checked, ...restOfProps },
                                }: {
                                  field: CheckboxProps;
                                }) => (
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        {...restOfProps}
                                        defaultChecked={defaultTaxonomyFilters.includes(
                                          title
                                        )}
                                      />
                                    }
                                    label={title}
                                  />
                                )}
                              </Field>
                            </div>
                          ))}
                      </Grid>
                      <Grid className={styles.checkboxGroupRight} item xs={6}>
                        {collections
                          ?.slice(Math.ceil(collections.length / 2))
                          ?.map((title: string) => (
                            <div key={title}>
                              <Field
                                name="checkboxes"
                                type="checkbox"
                                value={title}
                              >
                                {({
                                  field: { checked, ...restOfProps },
                                }: {
                                  field: CheckboxProps;
                                }) => (
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        {...restOfProps}
                                        defaultChecked={defaultTaxonomyFilters.includes(
                                          title
                                        )}
                                        color="primary"
                                      />
                                    }
                                    label={title}
                                  />
                                )}
                              </Field>
                            </div>
                          ))}
                      </Grid>
                    </Grid>
                  )}
                  <Box className={styles.buttonsContainer}>
                    <Button variant="outlinedSmall" onClick={handleGoBack}>
                      Back
                    </Button>
                    <Button
                      disabled={cardsToShow.length > 0 && !cardIdSelected}
                      style={{ borderRadius: "12px" }}
                      type="submit"
                      variant="containedSmall"
                    >
                      Next
                    </Button>
                  </Box>
                </Box>
              </Form>
            );
          }}
        </Formik>
      </Box>
    </Box>
  );
};

export default ProviderFiltersView;
